import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";

type Msg = { role: "user" | "assistant"; content: string };

const SESSION_KEY = "rl_chat_session";

function getSessionId() {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

// Render assistant reply: turn internal route links into <Link>s
function renderReply(text: string) {
  const parts = text.split(/(\/(?:presupuesto|inspeccion-de-rocodromos|contacto))/g);
  return parts.map((part, i) => {
    if (/^\/(presupuesto|inspeccion-de-rocodromos|contacto)$/.test(part)) {
      return (
        <Link key={i} to={part} className="text-primary underline font-medium">
          {part}
        </Link>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

export default function ChatWidget() {
  const { language } = useLanguage();
  const [enabled, setEnabled] = useState(true);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [teaser, setTeaser] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Show teaser bubble after a few seconds (once per session)
  useEffect(() => {
    if (sessionStorage.getItem("rl_chat_teaser")) return;
    const t = setTimeout(() => setTeaser(true), 6000);
    return () => clearTimeout(t);
  }, []);

  const dismissTeaser = () => {
    setTeaser(false);
    sessionStorage.setItem("rl_chat_teaser", "1");
  };

  // Read enabled flag
  useEffect(() => {
    supabase
      .from("chatbot_settings")
      .select("enabled")
      .eq("id", 1)
      .single()
      .then(({ data }) => {
        if (data && !data.enabled) setEnabled(false);
      });
  }, []);

  // Initial greeting
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([
        {
          role: "assistant",
          content:
            language === "eu"
              ? "Kaixo! Rock-Lineko laguntzailea naiz. Zertan lagundu diezazuket? (arautegia, ikuskapenak, aurrekontuak…)"
              : "Hola, soy el asistente de Rock-Line. ¿En qué puedo ayudarte? (normativa, inspecciones, presupuestos…)",
        },
      ]);
    }
  }, [open, language, messages.length]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  if (!enabled) return null;

  const send = async (text?: string) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    setInput("");
    const next = [...messages, { role: "user" as const, content }];
    setMessages(next);
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("chat", {
        body: { messages: next, sessionId: getSessionId() },
      });
      if (error) throw error;
      if (data?.disabled) {
        setMessages((m) => [...m, { role: "assistant", content: data.message }]);
        setEnabled(false);
        return;
      }
      setMessages((m) => [...m, { role: "assistant", content: data.reply ?? "..." }]);
    } catch {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "He tenido un problema técnico. Por favor, contáctanos en /contacto o por WhatsApp.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    { label: "Presupuesto", text: "Quiero solicitar un presupuesto" },
    { label: "Inspección", text: "Necesito inspección de un rocódromo" },
    { label: "WhatsApp", text: "¿Cómo os contacto por WhatsApp?" },
  ];

  return (
    <>
      {/* Teaser bubble */}
      {!open && teaser && (
        <div className="fixed bottom-24 right-6 z-50 max-w-[16rem] bg-background border border-border rounded-2xl rounded-br-sm shadow-xl p-3 animate-in fade-in slide-in-from-bottom-2">
          <button
            onClick={dismissTeaser}
            aria-label="Cerrar aviso"
            className="absolute -top-2 -left-2 bg-muted border border-border rounded-full p-1"
          >
            <X className="w-3 h-3" />
          </button>
          <button
            onClick={() => {
              dismissTeaser();
              setOpen(true);
            }}
            className="text-sm text-left"
          >
            {language === "eu"
              ? "Ikuskapena behar duzu? Galdetu iezadazu 👋"
              : "¿Necesitas una inspección? Pregúntame 👋"}
          </button>
        </div>
      )}

      {/* Trigger button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Abrir asistente"
          className="fixed bottom-6 right-6 z-50 bg-primary text-primary-foreground rounded-full pl-4 pr-5 py-3 shadow-lg hover:scale-105 transition-transform flex items-center gap-2"
        >
          <MessageCircle className="w-6 h-6" />
          <span className="text-sm font-medium">
            {language === "eu" ? "Laguntzailea" : "Asistente"}
          </span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-80 sm:w-96 h-[26rem] sm:h-[32rem] bg-background border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden">
          <div className="bg-primary text-primary-foreground px-4 py-3 flex items-center justify-between">
            <div>
              <div className="font-semibold">Asistente Rock-Line</div>
              <div className="text-xs opacity-90">Normativa · Inspecciones · Presupuestos</div>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Cerrar">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-muted/30">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-primary text-primary-foreground rounded-br-sm"
                      : "bg-background border border-border rounded-bl-sm"
                  }`}
                >
                  {m.role === "assistant" ? renderReply(m.content) : m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-background border border-border rounded-2xl px-3 py-2">
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                </div>
              </div>
            )}
            {messages.length === 1 && !loading && (
              <div className="flex flex-wrap gap-2 pt-2">
                {quickActions.map((q) => (
                  <button
                    key={q.label}
                    onClick={() => send(q.text)}
                    className="text-xs px-3 py-1.5 rounded-full border border-border bg-background hover:bg-accent"
                  >
                    {q.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="border-t border-border p-2 flex gap-2 bg-background"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              maxLength={1000}
              placeholder="Escribe tu pregunta…"
              className="flex-1 px-3 py-2 text-sm bg-muted rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={loading}
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="bg-primary text-primary-foreground rounded-lg px-3 disabled:opacity-50"
              aria-label="Enviar"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
